import {handleError} from "helpers"
import {createContext, useCallback, useContext, useEffect, useMemo, useRef, useState} from "react"
import type {ReactNode} from "react"

type RecordingPlaybackContextValue = {
    playingUrl: string | null
    togglePlay: (url: string) => void
    getAudioElement: () => HTMLAudioElement | null
}

const RecordingPlaybackContext = createContext<RecordingPlaybackContextValue | null>(null)

export function RecordingPlaybackProvider({children}: {children: ReactNode}) {
    const audioRef = useRef<HTMLAudioElement | null>(null)
    const [playingUrl, setPlayingUrl] = useState<string | null>(null)

    useEffect(() => {
        const audio = new Audio()
        audioRef.current = audio
        const onEnded = () => setPlayingUrl(null)
        const onError = () => setPlayingUrl(null)
        audio.addEventListener("ended", onEnded)
        audio.addEventListener("error", onError)
        return () => {
            audio.pause()
            audio.removeEventListener("ended", onEnded)
            audio.removeEventListener("error", onError)
            audioRef.current = null
        }
    }, [])

    const togglePlay = useCallback(
        (url: string) => {
            const audio = audioRef.current
            if (!audio) {
                return
            }
            if (playingUrl === url && !audio.paused) {
                audio.pause()
                setPlayingUrl(null)
                return
            }
            audio.pause()
            audio.src = url
            setPlayingUrl(url)
            audio.play().catch((err) => {
                setPlayingUrl(null)
                handleError(err)
            })
        },
        [playingUrl]
    )

    const getAudioElement = useCallback(() => audioRef.current, [])

    const value = useMemo(() => ({playingUrl, togglePlay, getAudioElement}), [playingUrl, togglePlay, getAudioElement])

    return <RecordingPlaybackContext.Provider value={value}>{children}</RecordingPlaybackContext.Provider>
}

export function useRecordingPlayback(): RecordingPlaybackContextValue {
    const ctx = useContext(RecordingPlaybackContext)
    if (!ctx) {
        throw new Error("useRecordingPlayback must be used within RecordingPlaybackProvider")
    }
    return ctx
}
